import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const reclaimDealSizingWindow = z.enum([
  "week",
  "twoWeeks",
  "month",
  "year",
  "allTime",
]);
export type ReclaimDealSizingWindow = z.infer<typeof reclaimDealSizingWindow>;

export const reclaimDealSizingByCurrencySchema = z.object({
  currency: z.string(),
  totalChargebacks: z.number(),
  dealSize: z.number(),
});

export interface ReclaimDealSizingByCurrency extends z.infer<
  typeof reclaimDealSizingByCurrencySchema
> {}

export const getReclaimDealSizing = unaryRpc({
  name: "getReclaimDealSizing" as const,
  input: z.object({
    /** Window applied to chargeback volume (initiated_at). Overview tile
     * uses 'year' to smooth out seasonal spikes. Defaults to 'year'. */
    volumeWindow: reclaimDealSizingWindow.optional(),
    /** Window applied to win-rate (ai_evidence_generated_at). Overview
     * tile uses 'month' to reflect current-state capability.
     * Defaults to 'month'. */
    winRateWindow: reclaimDealSizingWindow.optional(),
  }),
  output: z.object({
    feeRate: z.number(),
    winRate: z.number(),
    won: z.number().int(),
    lost: z.number().int(),
    /** Win rate from the immediately preceding period (e.g. days 31–60
     * ago when winRateWindow=month). Null when winRateWindow is "allTime"
     * or when the prior period had no finalized AI-touched disputes. */
    priorWinRate: z.number().nullable(),
    /** Signed percentage-point delta vs. priorWinRate. Null when
     * priorWinRate is null. */
    winRateChangePoints: z.number().int().nullable(),
    /** Won count for disputes where evidence_source = 'disputely', same window. */
    disputelyWon: z.number().int(),
    /** Lost count for disputes where evidence_source = 'disputely', same window. */
    disputelyLost: z.number().int(),
    /** Win rate (0–1) for Disputely-sourced disputes, same window. 0 when no data. */
    disputelyWinRate: z.number(),
    /** Won count for disputes where evidence_source != 'disputely' (Redo), same window. */
    redoWon: z.number().int(),
    /** Lost count for disputes where evidence_source != 'disputely' (Redo), same window. */
    redoLost: z.number().int(),
    /** Win rate (0–1) for Redo-sourced disputes (evidence_source != 'disputely'), same window. 0 when no data. */
    redoWinRate: z.number(),
    byCurrency: z.array(reclaimDealSizingByCurrencySchema),
  }),
});
